/**
 * =============================================================================
 * SHARED CONSTANTS
 * =============================================================================
 *
 * Single Source of Truth for validation rules, ID formats, and configuration
 * constants used across all packages (worker, admin-ui, storefront-widget).
 *
 * ARCHITECTURE REFERENCE:
 *   - BRIEFING.md §1.2 (Type Source of Truth)
 *   - PHASE_0B_SPEC.md Appendix C (ID Formats)
 *
 * ⚠️ CHANGES TO THIS FILE affect validation across the entire application.
 *    Update documentation when modifying these values.
 *
 * =============================================================================
 */

// =============================================================================
// FILE UPLOAD VALIDATION
// =============================================================================

export const FILE_VALIDATION = {
  /** Maximum file size in megabytes */
  MAX_SIZE_MB: 10,
  /** Maximum file size in bytes (10MB) */
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  /** Maximum number of images per question */
  MAX_IMAGES_PER_QUESTION: 5,
  /**
   * Maximum number of images per customer reply (ADR-022 §4/§14 D-RC — owner
   * default 5). PR3 enforces this as a CONSTANT clamp; the tenant-customizable
   * `maxImagesPerReply` setting (clamp 0..ceiling, 0 disables) arrives with the
   * §10.1 settings resolver in PR4b, mirroring how `maxImagesPerSubmission`
   * clamps against MAX_IMAGES_PER_QUESTION today.
   */
  MAX_IMAGES_PER_REPLY: 5,
  /**
   * Maximum number of images per STAFF-authored reply (ADR-022 §4/§10.1/§14
   * D-RC — staff get a higher allowance than customers, default 10, always
   * within the per-tenant aggregate ceiling D-QUOTA). This constant is the
   * HARD ceiling the tenant-customizable `maxImagesPerStaffReply` setting
   * clamps against (PR4b §10.1 resolver — `mediaSettings.ts`); the admin
   * upload route passes the resolved value as the atomic INSERT…SELECT cap
   * bound, which is how the ADR's "author-type-aware branch" is enforced
   * (author type is fixed per endpoint: staff-authored replies are never
   * guest-uploadable and vice versa, so the per-call bound IS the branch).
   */
  MAX_IMAGES_PER_STAFF_REPLY: 10,
  /** Allowed MIME types for image uploads */
  ALLOWED_MIME_TYPES: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ] as const,
  /** Allowed file extensions (for validation against filename) */
  ALLOWED_EXTENSIONS: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'] as const,
} as const;

/** Type for allowed MIME types - derived from constants */
export type AllowedMimeType = (typeof FILE_VALIDATION.ALLOWED_MIME_TYPES)[number];

/**
 * Per-tenant aggregate media ceiling (ADR-022 §14 D-QUOTA — RESOLVED with PR3,
 * owner-approved 2026-07-03; Launch Readiness Ledger §4 conditional pre-VISIBLE
 * blocker). Fail-closed cost/abuse guardrail enforced inside the SAME atomic
 * `INSERT…SELECT` that enforces the per-entity caps, on EVERY public upload
 * path — there is no separate check that can fail open (a D1 error means no
 * insert, i.e. the upload is rejected).
 *
 * BOTH ceilings apply — whichever trips first blocks the upload:
 *  - `MAX_ATTACHMENTS` bounds D1 row growth, moderation-queue workload, and
 *    CF Images transform spend (~1-2 transforms per upload vs the 5,000/month
 *    free tier).
 *  - `MAX_TOTAL_BYTES` bounds R2 storage spend (canonical `file_size` bytes;
 *    the 256px thumb adds <2% overhead and is deliberately not double-counted).
 *
 * COUNTING PREDICATE (owner-approved, the load-bearing choice): the quota
 * counts EVERY `media_type='IMAGE'` row for the tenant REGARDLESS of
 * `is_deleted`/`moderation_status`/`archived_at` — "quota releases only when
 * bytes actually leave R2." Soft-delete retains the R2 object today (no byte
 * purge exists until PR4-purge / the PR7 E.2 executor), so excluding deleted
 * rows would let an attacker upload-to-quota, self-delete (D-RC), and repeat —
 * unbounded R2 growth via the exact IP-rotation abuse D-QUOTA exists to stop.
 * The per-question/per-reply CAP predicates keep counting `is_deleted=0` only
 * (unchanged — so a self-delete still frees the cap slot, the D-RC remedy).
 * The byte-purge executor (PR4-purge/PR7) owns the release mechanics when it
 * lands.
 *
 * NUMBERS (owner-approved 2026-07-03): 1,000 attachments / 2 GB. Researched
 * rationale: comparable review/UGC apps (Judge.me, Loox, Stamped, Yotpo,
 * Okendo) treat customer media as effectively unlimited and meter orders or
 * emails instead — so this ceiling's only job is an abuse guardrail and must
 * be invisible to legitimate merchants. 2 GB ≈ 500-1,000 image-bearing
 * questions (years of headroom for a parts-Q&A storefront) at R2's
 * $0.015/GB-month ≈ $0.03/tenant-month if maxed; worst-case hostile spend is
 * bounded at 10 GB (1,000 × the 10 MB per-file cap). Tenant-tunable override
 * lands with the §10.1 settings resolver (PR4b); until then this constant is
 * the single revision point. Per-request D1 COUNT/SUM is acceptable at
 * controlled-launch scale; DO/counter-assisted accounting is the recorded
 * scale trigger (ADR-022 §14 D-QUOTA round-2 caveat), not built speculatively
 * (ADR-018).
 */
export const TENANT_MEDIA_QUOTA = {
  /** Maximum total image attachments per tenant (all statuses, incl. soft-deleted). */
  MAX_ATTACHMENTS: 1000,
  /** Maximum total stored canonical bytes per tenant (2 GB). */
  MAX_TOTAL_BYTES: 2 * 1024 * 1024 * 1024,
} as const;

// =============================================================================
// CONTENT VALIDATION
// =============================================================================

export const CONTENT_VALIDATION = {
  /** Minimum title length in characters */
  MIN_TITLE_LENGTH: 10,
  /** Maximum title length in characters */
  MAX_TITLE_LENGTH: 200,
  /** Minimum description length in characters */
  MIN_DESCRIPTION_LENGTH: 20,
  /** Maximum description length in characters */
  MAX_DESCRIPTION_LENGTH: 10000,
  /** Maximum slug length (for URL-friendly titles) */
  MAX_SLUG_LENGTH: 80,
  /** Minimum reply body length in characters (P1-21) */
  MIN_REPLY_LENGTH: 10,
  /** Maximum reply body length in characters (P1-21) */
  MAX_REPLY_LENGTH: 10000,
  // ---------------------------------------------------------------------------
  // RFI request foundation (BL-QA-RFI-TABLES-001 / ADR-020 §5)
  // ---------------------------------------------------------------------------
  /** Minimum RFI request message length (the staff prose / anchor reply body) */
  MIN_RFI_MESSAGE_LENGTH: 10,
  /** Maximum RFI request message length */
  MAX_RFI_MESSAGE_LENGTH: 10000,
  /** Minimum number of items in an RFI request (must be non-empty) */
  MIN_RFI_ITEMS: 1,
  /** Maximum number of items in a single RFI request */
  MAX_RFI_ITEMS: 20,
  /** Minimum RFI item label length */
  MIN_RFI_ITEM_LABEL_LENGTH: 1,
  /** Maximum RFI item label length */
  MAX_RFI_ITEM_LABEL_LENGTH: 200,
  /** Maximum RFI item unit length (measurement items) */
  MAX_RFI_ITEM_UNIT_LENGTH: 32,
  /** Maximum number of options for a selection item */
  MAX_RFI_ITEM_OPTIONS: 20,
  /** Maximum length of a single selection option */
  MAX_RFI_ITEM_OPTION_LENGTH: 100,
  // ---------------------------------------------------------------------------
  // RFI response linkage (BL-QA-RFI-OVERLAY-LINKAGE-001 / ADR-020 §5 — PR C)
  // ---------------------------------------------------------------------------
  /**
   * Maximum length of a staff-recorded `value_text` on an RFI response link
   * (the text/measurement/selection answer captured alongside a linked reply).
   */
  MAX_RFI_RESPONSE_VALUE_TEXT_LENGTH: 1000,
} as const;

/**
 * Valid RFI item types (BL-QA-RFI-TABLES-001 / ADR-020 §5). Source of truth for
 * the `rfi_items.item_type` text-union; used for request validation.
 */
export const RFI_ITEM_TYPES = [
  'photo',
  'measurement',
  'text',
  'selection',
  'confirmation',
] as const;

/** Valid RFI item type values (derived from RFI_ITEM_TYPES). */
export type RfiItemTypeValue = (typeof RFI_ITEM_TYPES)[number];

// =============================================================================
// ID GENERATION
// =============================================================================

/**
 * ID prefixes for entity identification.
 * Format: {prefix}{nanoid(length)}
 *
 * @example
 * const questionId = `${ID_PREFIXES.QUESTION}${nanoid(ID_LENGTHS.QUESTION)}`;
 */
export const ID_PREFIXES = {
  QUESTION: 'q_',
  GUEST_TOKEN: 'gt_',
  ATTACHMENT: 'att_',
  REPLY: 'r_',
  RFI: 'rfi_',
  RFI_ITEM: 'rfi_item_',
  RFI_RESPONSE: 'rfi_resp_',
  REPUTATION: 'rep_',
} as const;

/**
 * ID lengths (nanoid character count, excluding prefix).
 */
export const ID_LENGTHS = {
  /** Question ID: q_ + 12 chars = 14 total */
  QUESTION: 12,
  /** Guest token: gt_ + 21 chars = 24 total (nanoid default) */
  GUEST_TOKEN: 21,
  /** Attachment ID: att_ + 12 chars = 16 total */
  ATTACHMENT: 12,
  /** Reply ID: r_ + 12 chars = 14 total */
  REPLY: 12,
  /** RFI ID: rfi_ + 12 chars = 16 total */
  RFI: 12,
  /** RFI item ID: rfi_item_ + 12 chars = 21 total */
  RFI_ITEM: 12,
  /** RFI response link ID: rfi_resp_ + 12 chars = 21 total */
  RFI_RESPONSE: 12,
  /** Reputation ID: rep_ + 12 chars = 16 total (P1-27) */
  REPUTATION: 12,
} as const;

/**
 * ID validation patterns.
 * Use these to validate IDs received from external sources.
 *
 * @example
 * const isValid = ID_PATTERNS.QUESTION.test(questionId);
 */
export const ID_PATTERNS = {
  QUESTION: /^q_[A-Za-z0-9_-]{12}$/,
  GUEST_TOKEN: /^gt_[A-Za-z0-9_-]{21}$/,
  ATTACHMENT: /^att_[A-Za-z0-9_-]{12}$/,
  REPLY: /^r_[A-Za-z0-9_-]{12}$/,
  RFI: /^rfi_[A-Za-z0-9_-]{12}$/,
  RFI_ITEM: /^rfi_item_[A-Za-z0-9_-]{12}$/,
  RFI_RESPONSE: /^rfi_resp_[A-Za-z0-9_-]{12}$/,
  REPUTATION: /^rep_[A-Za-z0-9_-]{12}$/,
  /**
   * Public-image URL version segment (ADR-022 §6.2 G7 versioned URLs). A
   * positive integer, length-bounded (1–10 digits, no leading zero) so a
   * forged/oversized value is rejected at the route boundary before any DB
   * lookup, while still covering the FULL realistic range of
   * `media_attachments.public_version` (default 1, bumped once per takedown in
   * PR4). The 10-digit bound (> 2^31) ensures the canonical emitter
   * (`publicImageUrl`) can never produce a *current-version* URL the route then
   * rejects — the equality check is the only revocation gate (PR2b round
   * Codex-FMP-PR2B-001).
   */
  PUBLIC_VERSION: /^[1-9][0-9]{0,9}$/,
} as const;

// =============================================================================
// COOKIE CONFIGURATION
// =============================================================================

/**
 * Guest token cookie settings.
 * Note: Actual cookie setting happens server-side; these are reference values.
 */
export const GUEST_TOKEN_COOKIE = {
  /** Cookie name */
  NAME: 'guest_token',
  /** Cookie max age in seconds (1 year) */
  MAX_AGE_SECONDS: 31536000,
  /** Cookie path */
  PATH: '/',
  /**
   * SameSite setting for same-site (local dev) delivery.
   * Used when the cookie is NOT served over HTTPS (dev), where `SameSite=None`
   * is invalid because browsers require it to be paired with `Secure`.
   */
  SAME_SITE: 'Lax' as const,
  /**
   * SameSite setting for cross-site delivery (production).
   *
   * BL-WIDGET-GUEST-TOKEN-IMAGE-UPLOAD-001: The storefront widget and the
   * Worker API are served from different sites (e.g. `*.mybigcommerce.com`
   * vs. the Worker domain). A `SameSite=Lax` cookie is NOT attached to
   * cross-site `fetch`/XHR subrequests, so the image-upload request that
   * immediately follows question submission would arrive without
   * `guest_token`. `SameSite=None` lets the browser attach the cookie to
   * credentialed cross-site requests; browsers require it to be paired with
   * `Secure`, which is always set in production (both sides are HTTPS).
   */
  SAME_SITE_CROSS_SITE: 'None' as const,
  /** HttpOnly flag - prevents XSS access */
  HTTP_ONLY: true,
  /** Secure flag (true in production, false in development) */
  SECURE_IN_PRODUCTION: true,
} as const;

// =============================================================================
// GUEST UPLOAD/MANAGE CAPABILITIES (ADR-024 §10.3/§11 — cross-site transport)
// =============================================================================

/**
 * Cross-site guest-identity capability transport (ADR-024, ACCEPTED 2026-07-05).
 *
 * The storefront widget and Worker API are on different registrable domains, so
 * the `HttpOnly` `guest_token` cookie is a THIRD-PARTY cookie that iOS Safari
 * (ITP), Firefox (ETP), and 3PC-restricted Chromium silently drop — breaking
 * customer photo upload on the majority of mobile browsers. The fix carries
 * ownership proof in a REQUEST HEADER (browsers strip cookies, not headers), as
 * an opaque signed handle backed by strongly-consistent D1 capability state. The
 * raw long-lived `guest_token` NEVER leaves the cookie / server — the handle
 * carries only a random `jti` + claims; the token is recovered server-side from
 * the D1 row. See ADR-024 §11 for the authoritative spec and resolved decisions.
 */
export const GUEST_CAPABILITY = {
  /**
   * Request header carrying the opaque upload-proof handle on the write (image
   * upload) leg. A custom (non-safelisted) header, so the request is always
   * CORS-preflighted — deliberately NOT a cookie (ITP-blocked) or a URL param
   * (leaks to history/Referer/CDN logs; ADR-024 §10.5 no-logging invariant).
   */
  UPLOAD_PROOF_HEADER: 'X-Upload-Proof',
  /**
   * Request header carrying the opaque MANAGE handle on the cookieless self-delete
   * leg (`DELETE /api/public/manage/images/:id`). Like the upload proof it is a
   * custom (non-safelisted) header, so the request is always CORS-preflighted.
   * Colocated here with {@link UPLOAD_PROOF_HEADER} so BOTH guest-capability proof
   * headers have a single source — the outer `/api/public/*` CORS answers every
   * nested router's preflight (mount order; see routes/public.ts), so any proof
   * header the browser sends MUST be advertised there or the actual request is
   * blocked cross-site (ADR-024 §11.6 round-3 fold).
   */
  MANAGE_PROOF_HEADER: 'X-Manage-Proof',
  /**
   * Default upload-capability lifetime in seconds (owner decision c, 5 min). Must
   * be ≥ p95 submit→last-upload latency incl. one partial-upload retry so
   * multi-photo submits don't self-inflict 400s. Env override:
   * `UPLOAD_PROOF_TTL_SECONDS`. Bounds a leaked handle's replay window (further
   * bounded by the MediaService per-target cap + per-IP rate limit).
   */
  UPLOAD_TTL_SECONDS: 300,
  /**
   * Extra uses granted above the hard per-target image constant
   * (`MAX_IMAGES_PER_QUESTION` / `MAX_IMAGES_PER_REPLY`). `remaining_uses` is a
   * REPLAY bound, NOT the image cap — MediaService's atomic
   * `INSERT…SELECT WHERE (count < tenant-resolved cap)` is the real, hard
   * per-target image ceiling, enforced independently. Sizing off the raw
   * constant (rather than the tenant-resolved clamp) is a deliberate
   * simplification (ADR-024 §11.3): it avoids a settings read at submit time and
   * is a safe UPPER bound — a tenant whose resolved cap is lower just has excess
   * replays rejected at the MediaService cap, never an over-upload. This headroom
   * lets the partial-upload RETRY UX work on cookie-blocked browsers.
   */
  UPLOAD_RETRY_HEADROOM: 3,
  /**
   * Default management-capability lifetime in seconds (read/self-delete leg,
   * decision d — 30 days). Backs the cookieless `/manage?token=…` magic-link so
   * ITP users retain a durable self-service view/delete path without per-tenant
   * DNS. Env override: `MANAGE_CAPABILITY_TTL_SECONDS`.
   */
  MANAGE_TTL_SECONDS: 2592000,
} as const;

/** Capability kinds persisted in `guest_capabilities.kind`. */
export const GUEST_CAPABILITY_KINDS = ['UPLOAD', 'MANAGE'] as const;
export type GuestCapabilityKind = (typeof GUEST_CAPABILITY_KINDS)[number];

/** Capability target kinds persisted in `guest_capabilities.target_type`. */
export const GUEST_CAPABILITY_TARGET_TYPES = ['question', 'reply'] as const;
export type GuestCapabilityTargetType = (typeof GUEST_CAPABILITY_TARGET_TYPES)[number];

// =============================================================================
// R2 STORAGE
// =============================================================================

/**
 * R2 bucket configuration.
 */
export const R2_BUCKET = {
  /** Default bucket name for media storage */
  NAME: 'find-my-part-media',
} as const;

/**
 * R2 key structure patterns.
 * Format: {storeHash}/questions/{questionId}/media/{variant}/{attachmentId}.{ext}
 *
 * @example
 * const key = `${storeHash}/questions/${questionId}/media/original/${attachmentId}.jpg`;
 */
export const R2_KEY_STRUCTURE = {
  /** Supported image variants */
  VARIANTS: ['original', 'thumb', 'webp'] as const,
  /** Default variant for uploads */
  DEFAULT_VARIANT: 'original',
  /**
   * Cache-Control stored on the R2 object's httpMetadata at upload time.
   * ADR-022 §6.2 (G7 / D-CACHE-REVOCATION): a BOUNDED max-age, NOT the old
   * unbounded 1-year `immutable`. The worker serve path sets its own
   * `Cache-Control` explicitly (see `PUBLIC_IMAGE_SERVE_CACHE_CONTROL`), so this
   * metadata never reaches a client. Applies to objects uploaded from PR2b
   * onward; objects uploaded earlier retain the old 1-year value in their
   * stored httpMetadata — inert (never served), but a metadata backfill can
   * ride with the PR4 purge executor if byte-level hygiene is wanted.
   */
  CACHE_CONTROL: 'public, max-age=3600',
} as const;

/** Type for R2 image variants */
export type R2ImageVariant = (typeof R2_KEY_STRUCTURE.VARIANTS)[number];

/**
 * Header-first dimension bound (ADR-022 §6.2 G2 decompression-bomb guard /
 * §14 D-DIM, adopted 2026-07-03 — PR2c decision record). Declared dimensions
 * are read decode-free from the format header (JPEG SOFn / PNG IHDR /
 * WebP VP8(L/X) / HEIC `ispe`) and enforced BEFORE any decode, transform, or
 * binding call → `IMAGE_DIMENSIONS_EXCEEDED`. This is a bomb guard, and today
 * it is the ONLY dimension control: widget client-side compression is a PR5
 * deliverable (ADR-022 §12), not yet built — so opt-in high-resolution phone
 * modes (48MP+ ProRAW/HEIF Max; default fused outputs are 12–24MP and pass)
 * reject at this ceiling until PR5 client compression or the deferred D-DIM
 * stored-downscale lands (backbone review fold: the earlier claim that the
 * widget already caps at 1920px was wrong). Ceiling per ADR-022 §14 D-DIM
 * (~40MP rec.); the owner can revisit the number in this one constant.
 */
export const IMAGE_DIMENSION_LIMITS = {
  /** Hard reject: declared `width × height` above this pixel count. */
  MAX_PIXELS: 40_000_000,
} as const;

/**
 * Thumb variant generation parameters (ADR-022 §8 G2 / §14 D-TH, resolved
 * 2026-07-03 — PR2c decision record: pre-generated in R2 at upload).
 *
 * 256 px long edge covers the largest current consumer (admin queue thumbnails,
 * 120 px CSS at 2× DPR = 240) with margin and matches the repo's only historical
 * thumbnail figure (`?w=256`, ARCHITECTURE §11.3 sketch). Generation uses
 * `fit: scale-down` (never enlarges, never crops — content-faithful for
 * moderation and parts-Q&A contexts) and preserves the canonical's format.
 * Images whose long edge is already ≤ this target skip generation entirely
 * (a same-size re-encode wastes a CF transform) and serve canonical bytes at
 * thumb URLs via the serve-path fallback.
 */
export const THUMBNAIL_VARIANT = {
  /** Long-edge target in pixels (`fit: scale-down`). */
  LONG_EDGE_PX: 256,
} as const;

/**
 * Variants accepted in the public `/:variant` path segment (PR2c). A closed
 * enum, validated at the route boundary BEFORE any DB lookup (unknown variant
 * → 404, mirroring the `PUBLIC_VERSION` pattern). Deliberately minimal —
 * `original` is NOT aliased here (the canonical un-suffixed URL already serves
 * it) and `webp` stays unrouted until built: every additional valid URL form is
 * another cache key the G7 purge enumeration must cover.
 */
export const PUBLIC_IMAGE_PATH_VARIANTS = ['thumb'] as const satisfies readonly R2ImageVariant[];
export type PublicImagePathVariant = (typeof PUBLIC_IMAGE_PATH_VARIANTS)[number];

/**
 * Public image serve cache policy (ADR-022 §6.2 G7 / D-CACHE-REVOCATION).
 *
 * The takedown control is **origin denial + BOUNDED TTL** (NOT unbounded
 * `immutable`): a Hide/Reject makes the origin 404 the URL, and any
 * already-delivered cached copy ages out within `PUBLIC_IMAGE_MAX_AGE_SECONDS`.
 * Instant edge eviction (the durable purge executor) rides with PR4, keyed off
 * the `Cache-Tag` this serve path emits.
 */
export const PUBLIC_IMAGE_MAX_AGE_SECONDS = 3600;

/** `Cache-Control` value set on every PUBLIC image serve response. */
export const PUBLIC_IMAGE_SERVE_CACHE_CONTROL = `public, max-age=${PUBLIC_IMAGE_MAX_AGE_SECONDS}`;

/**
 * Upper bound on `attachmentIds` per POST /api/admin/media/bulk request
 * (ADR-022 §10(C) bulk moderation — per-item results). Bounded well below the
 * D1 parameter limit; a larger selection is split client-side.
 */
export const MEDIA_MODERATION_BULK_MAX = 50;

// =============================================================================
// MEDIA MODERATION VOCABULARIES (ADR-022 §5 / BL-IMG-ADMIN-MODERATION-001)
// =============================================================================
// Client-visible mirrors of the media moderation unions defined in the worker
// Drizzle schema (`packages/worker/src/db/schema.ts`) — a deliberate duplicate
// following the `ReplyModerationStatus` precedent, so the admin UI never
// imports the worker schema. Value arrays exist for runtime validation at the
// route/UI boundary (the `AUDIT_ACTIONS` pattern); the derived unions are the
// SSOT for API DTOs. Keep the value sets in lockstep with the schema.

/**
 * Media moderation status values (`media_attachments.moderation_status`).
 * The SOLE public-visibility source since PR2a (ADR-022 §5).
 */
export const MEDIA_MODERATION_STATUSES = [
  'PENDING_REVIEW',
  'APPROVED',
  'REJECTED',
  'HIDDEN',
] as const;
export type MediaModerationStatus = (typeof MEDIA_MODERATION_STATUSES)[number];

/**
 * Pure, shared status rule (ADR-022 §14 D-ADMIN-REPLY-DISPLAY / D5): whether an
 * admin-visible media attachment is eligible to be linked as RFI evidence.
 * STATUS ONLY — an attachment is linkable exactly when it is publicly-approved
 * AND not under a legal hold. This is the client/server single source of truth
 * so the RFI picker's selectable set and the backend's authoritative write-path
 * gate (`RfiService.linkResponses`) cannot drift.
 *
 * It deliberately owns ONLY the status axis. The server-only liveness/ownership
 * truths it alone can assert — `store_hash`, not-deleted, not-archived, exact
 * reply/question ownership, media↔reply question-linkage equality — stay in the
 * backend and are NEVER moved into this client package.
 *
 * (Runtime helpers shipping from `packages/types` is settled convention:
 * `isValidAdminTransition` / `publicImageUrl` / `isPublicLaunchMode`.)
 */
export function isRfiLinkableAdminMedia(media: {
  moderationStatus: MediaModerationStatus;
  legalHold: boolean;
}): boolean {
  return media.moderationStatus === 'APPROVED' && !media.legalHold;
}

/**
 * Server-derived author-trust dimension (`media_attachments.uploaded_by_type`).
 * Never client-asserted.
 */
export const MEDIA_AUTHOR_TYPES = ['GUEST', 'CUSTOMER', 'ADMIN'] as const;
export type MediaAuthorType = (typeof MEDIA_AUTHOR_TYPES)[number];

/**
 * Controlled rejection-reason vocabulary (`media_attachments.rejection_reason`).
 * Admin-only — NEVER rendered verbatim to a poster (anti-tip-off, ADR-022 §5).
 * `SUSPECTED_ILLEGAL` is special: the server remaps the transition to
 * HIDDEN + legal hold (D-LEGALHOLD) — never a byte-destructive reject.
 */
export const MEDIA_REJECTION_REASONS = [
  'NOT_RELEVANT',
  'LOW_QUALITY',
  'SPAM',
  'NSFW',
  'PERSONAL_INFO',
  'SUSPECTED_ILLEGAL',
  'OTHER',
] as const;
export type MediaRejectionReason = (typeof MEDIA_REJECTION_REASONS)[number];

/**
 * Controlled question-rejection reason vocabulary (ADR-025 §7.1.2 / OD-19,
 * `BL-ADMIN-WORKBENCH-IA-001B`). Mirrors the media pattern above: a REQUIRED
 * controlled code — never free text — recorded in the `QUESTION_REJECTED`
 * audit row's metadata (no schema column; the audit row is the record).
 * An OPTIONAL free-text note may accompany the code. When the rejection
 * applies, A-Q1 stores it in audit metadata; when a guarded rejection attempt
 * is stale, only the controlled code plus `noteRecorded` marker persist. The
 * ADR-023 display-safe projection excludes the note in either case (it
 * allow-lists named scalars only — the note is never rendered). Admin-only;
 * never surfaced to a poster.
 */
export const QUESTION_REJECTION_REASONS = [
  'NOT_RELEVANT',
  'SPAM',
  'DUPLICATE',
  'INAPPROPRIATE',
  'PERSONAL_INFO',
  'UNANSWERABLE',
  'OTHER',
] as const;
export type QuestionRejectionReason = (typeof QUESTION_REJECTION_REASONS)[number];

/** Max length of the optional rejection note (trimmed; longer → 400). */
export const QUESTION_REJECTION_NOTE_MAX_LENGTH = 500;

/**
 * Moderation action performable on a media attachment (ADR-022 §10(D)).
 * SHOW (PR4b, BL-IMG-ADMIN-AUTHORING-001) is the guarded HIDDEN → APPROVED
 * recovery of §10(B)'s "Show/Hide = guarded APPROVED ↔ HIDDEN"; REJECTED
 * stays terminal. SHOW is VETOED on a legal-held row (D-LEGALHOLD — a
 * SUSPECTED_ILLEGAL attachment is HIDDEN + held, and re-publishing held
 * evidence is never allowed; release the hold first via the runbook).
 */
export const MEDIA_MODERATION_ACTIONS = ['APPROVE', 'REJECT', 'HIDE', 'SHOW'] as const;
export type MediaModerationAction = (typeof MEDIA_MODERATION_ACTIONS)[number];

/**
 * Stable `Cache-Tag` for an attachment's served bytes across ALL versions and
 * variants (ADR-022 §6.2 G7). Emitting it now lets the PR4 purge executor evict
 * everything with one tag-purge (Cloudflare Enterprise). On lower tiers the
 * PR4 executor must instead **enumerate every previously-emitted URL** (the
 * ADR's G7 completeness rule). Since BL-IMG-PUBLIC-DELIVERY-001 the canonical
 * public delivery URL is the header-free TENANT-IN-PATH shape
 * `/api/public/stores/:storeHash/images/:attachmentId/v/:publicVersion`
 * (emitted absolute against `WORKER_BASE_URL`), so the enumeration set is, per
 * attachment: `/api/public/stores/:storeHash/images/:attachmentId/v/1..N`, EACH
 * version's **`/v/k/thumb` path variant (the PR2c-emitted thumbnail shape)**
 * AND its historical `?size=thumb` query variant (a distinct CDN cache key —
 * emitted by PR2b/PR2c-era payloads and kept serving as an alias), the
 * un-versioned tenant-in-path alias `/api/public/stores/:storeHash/images/:attachmentId`
 * (+ its `?size=thumb` form), AND the
 * pre-existing header-based routes `/api/public/images/:attachmentId(/v/N)`
 * (kept as documented aliases) **including their historical `?size=thumb` forms**
 * (the PR2b detail/list `thumbnailUrl` emitted `/api/public/images/:id/v/:n?size=thumb`
 * before this branch repointed emission to the tenant-in-path shape) — every
 * alias keeps its cache key repopulating, so `/v/1..N` on one shape alone is NOT
 * sufficient (Fable-5 round findings). Format: `media-{storeHash}-{attachmentId}`.
 * (When PR4 introduces a CF Cache Rule, note that origin 404s carry no
 * `Cache-Control` and are not browser-cached, and that percent-encoded /
 * arbitrary-query variants of these URLs mint additional un-enumerable cache
 * keys — a further argument for tag-purge over URL enumeration on that path.)
 *
 * NOTE: worker-generated serve responses are not edge-cached by default (the
 * serve path never calls `caches.default.put`), so the tag is forward-compatible
 * metadata until PR4 populates the CF edge cache (or a Cache Rule does); until
 * then the exposure model is browser caches bounded by the serve TTL.
 *
 * Defense-in-depth (PR2b round GPT-F3/Codex/DeepSeek): both inputs are already
 * safe on the serve path (`attachmentId` is `ID_PATTERNS.ATTACHMENT`-validated;
 * `storeHash` only reaches here after a tenant-scoped DB match, and stored BC
 * hashes are alphanumeric), but this helper additionally constrains each part to
 * `[A-Za-z0-9_-]` so a well-formed, injection-free header value is guaranteed at
 * the point of construction regardless of the caller.
 */
export function mediaCacheTag(storeHash: string, attachmentId: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '');
  return `media-${safe(storeHash)}-${safe(attachmentId)}`;
}

/**
 * Build the PUBLIC serve URL for an approved image (ADR-022 §6.2 versioned URLs
 * + BL-IMG-PUBLIC-DELIVERY-001 header-free, absolute delivery). The single
 * source of truth for every public image-URL emitter (question detail, list
 * thumbnail, MSPL renderer, upload echo) so the shape cannot drift.
 *
 * Shape: `/api/public/stores/${storeHash}/images/${attachmentId}/v/${publicVersion}`.
 *
 * BL-IMG-PUBLIC-DELIVERY-001 — a plain browser `<img src>` cannot load a public
 * image unless BOTH problems are fixed here at the one emitter:
 *   1. TENANT-IN-PATH — the tenant is carried in the URL path (mirrors the
 *      header-free sitemap route `/api/public/stores/:storeHash/sitemap.xml`),
 *      so the byte route needs NO `X-Store-Hash` header a bare `<img>` cannot
 *      send. `storeHash` is already public (widget `<script ?store=…>`, the
 *      sitemap path), so this is not a new disclosure.
 *   2. ABSOLUTE ORIGIN — `baseUrl` (the worker's own public origin,
 *      `env.WORKER_BASE_URL`) is prefixed so the URL resolves to the WORKER, not
 *      the page origin. A relative `src` resolves against the merchant storefront
 *      domain (MSPL pages on BC Pages V3) or the storefront origin (widget) —
 *      never the worker, where the bytes live — so it 404s cross-origin. When
 *      `baseUrl` is empty/omitted the URL stays relative (graceful fallback,
 *      mirroring EmailService's WORKER_BASE_URL-unset behaviour; production
 *      always sets it).
 *
 * A superseded `publicVersion` in a cached/old URL is origin-denied (404) by the
 * serve route. `variant: 'thumb'` appends the `/thumb` PATH segment (PR2c —
 * the ADR-anticipated `[/:variant]` shape, §6.2 G7): a path variant gets
 * strict closed-enum validation at the route boundary (an unknown variant
 * 404s loudly, where a typoed query param would silently serve the original)
 * and is a robust cache key against query-normalizing intermediaries. The
 * previously-emitted `?size=thumb` query form is KEPT as a serving alias
 * (D-URL-MIGRATION precedent — published MSPL pages embed it; those URLs now
 * return real thumb bytes), but is no longer emitted.
 */
export function publicImageUrl(
  storeHash: string,
  attachmentId: string,
  publicVersion: number,
  opts?: { variant?: 'thumb'; baseUrl?: string }
): string {
  const origin = opts?.baseUrl ? opts.baseUrl.replace(/\/+$/, '') : '';
  const base = `${origin}/api/public/stores/${storeHash}/images/${attachmentId}/v/${publicVersion}`;
  return opts?.variant === 'thumb' ? `${base}/thumb` : base;
}

// =============================================================================
// QUESTION STATUS
// =============================================================================

/**
 * All valid question status values as a constant array.
 * Use this for iteration, validation, and status dropdown population.
 *
 * @example
 * if (QUESTION_STATUSES.includes(userInput as QuestionStatus)) { // validate status
 * QUESTION_STATUSES.forEach(status => { ... }); // iterate statuses
 */
export const QUESTION_STATUSES = [
  'PENDING_REVIEW',
  'OPEN',
  'IN_PROGRESS',
  'NEEDS_MORE_INFO',
  'RESOLVED',
  'CLOSED_DUPLICATE',
  'CLOSED_REJECTED',
] as const;

/**
 * Valid question status values (derived from QUESTION_STATUSES array).
 * This type is the source of truth for status values across the application.
 */
export type QuestionStatus = (typeof QUESTION_STATUSES)[number];

/**
 * Human-readable labels for question statuses.
 * Use these for display in UI components.
 *
 * @example
 * const label = QUESTION_STATUS_LABELS['PENDING_REVIEW']; // 'Pending Review'
 */
export const QUESTION_STATUS_LABELS: Record<QuestionStatus, string> = {
  PENDING_REVIEW: 'Pending Review',
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  NEEDS_MORE_INFO: 'Needs More Info',
  RESOLVED: 'Resolved',
  CLOSED_DUPLICATE: 'Closed (Duplicate)',
  CLOSED_REJECTED: 'Rejected',
} as const;

/**
 * Question statuses visible to public users (approved content only).
 * PENDING_REVIEW and CLOSED_REJECTED are admin-only.
 *
 * Used for:
 * - GET /api/public/questions (list browsing)
 * - GET /api/public/questions/:id (single question)
 * - Sitemap generation
 *
 * @see PHASE_1_SPEC.md P1-20: Public Question Browsing
 */
export const PUBLIC_VISIBLE_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'NEEDS_MORE_INFO',
  'RESOLVED',
] as const;

/**
 * Type for public visible status values.
 */
export type PublicVisibleStatus = (typeof PUBLIC_VISIBLE_STATUSES)[number];

// =============================================================================
// AUDIT LOG (admin action audit trail)
// =============================================================================
//
// SSOT for the audit-log action + entity-type vocabularies. These domain enums
// are shared between the Drizzle schema (`audit_logs.action` / `.entity_type`
// `$type<…>()` column annotations) and the display-safe API DTOs in `api.ts`
// (`AuditLogView`), so they live here in `@find-my-part/types` and are
// re-exported from `db/schema.ts` (mirroring the `PublicLaunchMode` relocation
// precedent — see `launchMode.ts` and `db/schema.ts` header). The DB columns
// remain plain `text()`; these are TypeScript-level unions only, so adding a
// member is a type-only change requiring no migration. See CONVENTIONS.md §1.2.
//
// ⚠️ ORDER + MEMBERSHIP ARE LOAD-BEARING. The order matches the historical
// schema declaration order and is asserted by an audit-viewer tripwire test
// (`AUDIT_ACTIONS` length 28 / `AUDIT_ENTITY_TYPES` length 8). `AuditService`
// assigns string literals checked against `AuditAction`; dropping/renaming a
// member breaks typecheck + tests.

/**
 * All admin audit-log action types, in canonical (historical schema) order.
 * Derived type `AuditAction` is the SSOT consumed by the worker schema/service
 * and the display-safe API DTOs.
 *
 * Provenance of the later members (all type-only `text()` additions, no
 * migration):
 *   - `PARTICIPANT_SUPPORT_LOOKUP` — BL-ADMIN-PARTICIPANT-SUPPORT-LOOKUP-001
 *   - `PARTICIPANT_NOTIFICATION_RESUBSCRIBE` — BL-ADMIN-NOTIFICATION-RESUBSCRIBE-001
 *   - `CONSENT_RECORD_INSPECTION` — BL-PRIVACY-CONSENT-CAPTURE-001
 *   - `RFI_OPENED` — BL-QA-RFI-TABLES-001 (ADR-020 §5/§5.2)
 *   - `RFI_RESPONSE_LINKED` / `RFI_RESPONSE_UNLINKED` / `RFI_ITEM_REVIEWED` —
 *     BL-QA-RFI-OVERLAY-LINKAGE-001 (ADR-020 §5/§5.2, PR C)
 *   - `RFI_CANCELLED` — BL-QA-RFI-CANCEL-001 (ADR-020 §5.2, PR H)
 *   - `MEDIA_APPROVED` / `MEDIA_REJECTED` / `MEDIA_HIDDEN` —
 *     BL-IMG-ADMIN-MODERATION-001 (ADR-022 §5, PR4 media moderation)
 *   - `MEDIA_SELF_REMOVED` — BL-IMG-REPLY-UPLOAD-001 (ADR-022 §14 D-RC, PR3
 *     customer self-delete). The FIRST guest-actor audit action: the actor is
 *     the attachment's own uploader (guest-token-owned public path), so
 *     `adminEmail` carries the non-PII sentinel `MEDIA_SELF_SERVICE_AUDIT_ACTOR`
 *     rather than a verified admin identity — CONVENTIONS §2.8 is applied with
 *     the ownership predicate standing in for the capability gate.
 *   - `MEDIA_UPLOADED` / `MEDIA_REMOVED` — BL-IMG-ADMIN-AUTHORING-001
 *     (ADR-022 §5/§10, PR4b). `MEDIA_UPLOADED` records a STAFF-authored
 *     upload (`MEDIA_AUTHOR`-gated admin endpoints — auto-approved, so the
 *     upload IS a visibility event and is audited fail-loud); `MEDIA_REMOVED`
 *     records the admin soft-delete (`DELETE /api/admin/media/:id`,
 *     `MEDIA_MODERATE`-gated, D-LEGALHOLD-vetoed). SHOW deliberately has NO
 *     distinct action: the ADR §5 audit vocabulary derives from the RESULTING
 *     status (SHOW lands as `MEDIA_APPROVED`); the applied action is carried
 *     in bounded metadata (`moderationAction`) instead.
 *
 * `QUESTION_DELETED` / `REPLY_DELETED` are **reserved** — they have NO current
 * production writer (no delete endpoint is built). They remain in the canonical
 * vocabulary so the type/tripwire stay stable; the audit-viewer projection maps
 * both to a MINIMAL view (their would-be `previousValue` is an arbitrary blob),
 * so no PII can leak if a writer is ever added. Wire a real writer + a specific
 * projection when a delete slice lands.
 */
export const AUDIT_ACTIONS = [
  'QUESTION_APPROVED',
  'QUESTION_REJECTED',
  'QUESTION_DELETED',
  'QUESTION_STATUS_CHANGED',
  'QUESTION_TAGS_UPDATED',
  'QUESTION_PRODUCTS_UPDATED',
  'REPLY_CREATED',
  'REPLY_DELETED',
  'REPLY_MARKED_SOLUTION',
  'REPLY_UNMARKED_SOLUTION',
  'REPLY_APPROVED',
  'REPLY_REJECTED',
  'REPLY_HIDDEN',
  'SETTINGS_UPDATED',
  'PARTICIPANT_SUPPORT_LOOKUP',
  'PARTICIPANT_NOTIFICATION_RESUBSCRIBE',
  'CONSENT_RECORD_INSPECTION',
  'RFI_OPENED',
  'RFI_RESPONSE_LINKED',
  'RFI_RESPONSE_UNLINKED',
  'RFI_ITEM_REVIEWED',
  'RFI_CANCELLED',
  'MEDIA_APPROVED',
  'MEDIA_REJECTED',
  'MEDIA_HIDDEN',
  'MEDIA_SELF_REMOVED',
  'MEDIA_UPLOADED',
  'MEDIA_REMOVED',
] as const;

/**
 * Sentinel audit actor for guest-initiated media self-delete rows
 * (`MEDIA_SELF_REMOVED`). `audit_logs.admin_email` is NOT NULL and every other
 * writer stamps a verified admin identity; the self-delete actor is an
 * anonymous guest whose only identity is a bearer token that must never reach
 * D1 (D-DSAR). A fixed non-PII machine label keeps the column honest ("who did
 * this" = the uploader, via self-service) without inventing a fake email; the
 * uploader linkage itself lives on the media row (`uploaded_by_id`, versioned
 * HMAC digest).
 */
export const MEDIA_SELF_SERVICE_AUDIT_ACTOR = 'guest:self-service' as const;

/**
 * Sentinel audit actor for POSTURE-approved guest uploads (`MEDIA_UPLOADED`
 * under `imageModeration = AUTO_APPROVE` — PR4b backbone-review fold C3).
 * A posture-approved upload is a PUBLIC-content mutation with no acting
 * admin: the authority is the tenant's persisted posture, the actor is an
 * anonymous guest whose bearer token never reaches D1 (D-DSAR). Same
 * rationale as `MEDIA_SELF_SERVICE_AUDIT_ACTOR` — a fixed non-PII machine
 * label keeps `admin_email` honest; the uploader linkage lives on the media
 * row (`uploaded_by_id` digest).
 */
export const MEDIA_AUTO_APPROVE_AUDIT_ACTOR = 'guest:auto-approve' as const;

/**
 * Valid admin audit-log action values (derived from AUDIT_ACTIONS array).
 * Source of truth for the `audit_logs.action` column type and API DTOs.
 */
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * All admin audit-log entity types, in canonical (historical schema) order.
 *
 * `PARTICIPANT` identifies a notification participant keyed by a
 * privacy-preserving email hash (never the raw email). `RFI_REQUEST` /
 * `RFI_ITEM` / `RFI_RESPONSE` identify an official RFI request, a single RFI
 * item, and a response link respectively. `MEDIA_ATTACHMENT` identifies a
 * `media_attachments` row (`att_…` — ADR-022 §5 media moderation). All later
 * members are type-only `text()` additions (no migration).
 */
export const AUDIT_ENTITY_TYPES = [
  'QUESTION',
  'REPLY',
  'STORE_SETTINGS',
  'PARTICIPANT',
  'RFI_REQUEST',
  'RFI_ITEM',
  'RFI_RESPONSE',
  'MEDIA_ATTACHMENT',
] as const;

/**
 * Valid admin audit-log entity-type values (derived from AUDIT_ENTITY_TYPES).
 * Source of truth for the `audit_logs.entity_type` column type and API DTOs.
 */
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

// =============================================================================
// QUESTION STATUS TRANSITIONS (Phase 0B)
// =============================================================================

/**
 * Allowed status transitions for Phase 0B.
 * Other transitions are deferred to Phase 1+.
 *
 * Note: The QuestionStatus type is defined above in this file.
 * This constant defines the valid transition graph for Phase 0B.
 */
export const PHASE_0B_STATUS_TRANSITIONS = {
  PENDING_REVIEW: ['OPEN', 'CLOSED_REJECTED'] as const,
} as const;

/**
 * Full admin status transition graph (Phase 2).
 * Extends Phase 0B with all states reachable by admin actions.
 */
export const ADMIN_STATUS_TRANSITIONS: Record<string, readonly string[]> = {
  PENDING_REVIEW: ['OPEN', 'CLOSED_REJECTED'],
  OPEN: ['IN_PROGRESS', 'NEEDS_MORE_INFO', 'RESOLVED', 'CLOSED_DUPLICATE', 'CLOSED_REJECTED'],
  IN_PROGRESS: ['OPEN', 'NEEDS_MORE_INFO', 'RESOLVED', 'CLOSED_DUPLICATE'],
  NEEDS_MORE_INFO: ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED_DUPLICATE'],
  RESOLVED: ['OPEN'], // Reopen a resolved question
  CLOSED_DUPLICATE: ['OPEN'], // Reopen a duplicate
  CLOSED_REJECTED: ['OPEN'], // Reopen a rejected question
} as const;

/**
 * Check if a status transition is valid for admin actions.
 * Uses the full Phase 2 transition graph.
 */
export function isValidAdminTransition(fromStatus: string, toStatus: string): boolean {
  const allowed = ADMIN_STATUS_TRANSITIONS[fromStatus];
  return allowed?.includes(toStatus) ?? false;
}

/**
 * Check if a status transition is valid in Phase 0B.
 */
export function isValidPhase0BTransition(fromStatus: string, toStatus: string): boolean {
  const allowed =
    PHASE_0B_STATUS_TRANSITIONS[fromStatus as keyof typeof PHASE_0B_STATUS_TRANSITIONS];
  return allowed?.includes(toStatus as 'OPEN' | 'CLOSED_REJECTED') ?? false;
}

// =============================================================================
// WIDGET SURFACE MOUNT V1 (cross-package contract)
// =============================================================================
//
// Single source of truth for the DOM IDs the storefront widget looks for at
// bootstrap time. Both `PublicRendererService` (worker) and `bootstrap()`
// (storefront-widget) must agree on these strings — drift between them was
// the root cause of an Item #7 hydration failure on the OGS sandbox
// 2026-05-08, where the renderer emitted `<div id="fmp-widget-mount">` but
// the widget bootstrap only looked for `#find-my-part-app`. Anchoring both
// sides to these constants and asserting it in tests prevents recurrence.
// =============================================================================

/**
 * Canonical mount id for **MSPL Mode 2** raw-page bodies.
 *
 * `PublicRendererService.buildBody` emits `<div id="fmp-widget-mount" …>`
 * inside the raw-HTML fragment that the publisher POSTs to BC Pages V3.
 * A valid SurfaceMountV1 element MUST retain this id. Compatibility markup
 * at either recognized id remains in the candidate union; cardinality is
 * evaluated before any element may be selected.
 *
 * Renamed from `find-my-part-app` to `fmp-widget-mount` to namespace away
 * from common merchant CSS classes / ids.
 */
export const MSPL_MOUNT_ID = 'fmp-widget-mount';

/**
 * Legacy mount id used by merchant integrations that explicitly add a
 * `<div id="find-my-part-app">` to a Stencil template. The widget still
 * recognises this as a fallback so existing integrations don't break,
 * but new MSPL Mode 2 publishing should rely on `MSPL_MOUNT_ID`.
 */
export const LEGACY_MOUNT_ID = 'find-my-part-app';

/**
 * Mount id used in widget development (`pnpm dev:widget` standalone
 * harness). Never matches in production because the production build's
 * `isDevelopment()` returns false.
 */
export const DEV_MOUNT_ID = 'widget-mount';

/**
 * Production mount ids recognised by the storefront widget.
 *
 * This remains an ordered registry for compatibility call sites, but it is
 * NOT a winner-takes-first precedence rule. SurfaceMountV1 enumerates the
 * distinct-element union of both ids and both contract attributes; two
 * candidates fail closed rather than silently choosing either id.
 *
 * Tests in both packages reference this exact ordering so the rule is
 * enforced everywhere it matters.
 */
export const MOUNT_ID_PRECEDENCE = [MSPL_MOUNT_ID, LEGACY_MOUNT_ID] as const;
